import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtRelative } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { ShoppingBag, Check, X, Loader2, Plug, Zap, AlertTriangle, RefreshCw, ExternalLink, Code2, Copy, CircleDot } from "lucide-react";
import { toast } from "sonner";

export default function ShopifySettingsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_settings");
  const [search] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openConnect, setOpenConnect] = useState(false);
  const [shopInput, setShopInput] = useState("ieftinit-2841.myshopify.com");
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [reinstalling, setReinstalling] = useState(false);
  const [webhookLogs, setWebhookLogs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shopify/status");
      setStatus(data);
      if (data.connected && canManage) {
        try {
          const wl = await api.get("/shopify/webhooks/recent");
          setWebhookLogs(wl.data);
        } catch {}
        runHealth();
      }
    } finally { setLoading(false); }
  };

  const runHealth = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/shopify/health");
      setHealth(data);
    } catch (e) {
      setHealth({ functional_ok: false, checks: [], error: formatApiError(e) });
    } finally { setHealthLoading(false); }
  };

  const reinstall = async () => {
    setReinstalling(true);
    try {
      const { data } = await api.post("/shopify/reinstall");
      window.location.href = data.install_url;
    } catch (e) {
      setReinstalling(false);
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (search.get("connected") === "1") {
      toast.success(`Conectat cu succes la ${search.get("shop")}`);
      load();
    }
  }, [search]);

  const startInstall = async () => {
    if (!shopInput.trim().endsWith(".myshopify.com")) {
      return toast.error("Domeniu invalid - trebuie să se termine cu .myshopify.com");
    }
    setConnecting(true);
    try {
      const { data } = await api.get(`/shopify/install`, { params: { shop: shopInput.trim() } });
      window.location.href = data.install_url;
    } catch (e) {
      setConnecting(false);
      toast.error(formatApiError(e));
    }
  };

  const testConn = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/shopify/test");
      toast.success(`✓ Conectat la ${data.shop} (${data.plan || "free"})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };
  void testConn; // kept for backward-compat (unused after health probe replaced it)

  const disconnect = async () => {
    if (!confirm("Sigur vrei să deconectezi magazinul Shopify? Tokenul va fi marcat ca dezinstalat.")) return;
    try {
      const shop = status?.shop;
      await api.post("/shopify/disconnect", null, { params: shop ? { shop } : {} });
      toast.success("Deconectat");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "https://dashboard.ieftinit.ro";

  const snippets = [
    {
      key: "returns",
      title: "Retururi",
      filename: "ieftinit-returns",
      sectionName: "IEFTINIT Retururi",
      description: "Pentru pagina de retururi din magazinul Shopify.",
      code: `{% comment %}
  IEFTINIT Control Hub — Return Request Section (Online Store 2.0 compatible)
  Drop this snippet into your theme and add the section to any page from the Theme Customizer.
  Auto-iframes the hosted return form in EMBED mode (no header, light theme, no branding).
{% endcomment %}

<section class="ieftinit-returns" style="padding: 24px 0; background: {{ section.settings.bg_color | default: '#ffffff' }};">
  <div style="max-width: 960px; margin: 0 auto; padding: 0 16px;">
    {% if section.settings.heading != blank %}
      <h2 style="text-align:center; font-size: clamp(24px, 4vw, 32px); margin: 0 0 8px; color: {{ section.settings.text_color | default: '#0a0a0c' }}; font-weight: 700; letter-spacing: -0.02em;">
        {{ section.settings.heading }}
      </h2>
    {% endif %}
    {% if section.settings.subheading != blank %}
      <p style="text-align:center; opacity:.65; margin: 0 0 24px; color: {{ section.settings.text_color | default: '#0a0a0c' }};">
        {{ section.settings.subheading }}
      </p>
    {% endif %}
    <iframe
      src="${origin}/retur?embed=1"
      style="width:100%; min-height: 900px; border:0; background: transparent; display: block;"
      title="Formular retur IEFTINIT"
      loading="lazy"
      allow="clipboard-write"
      scrolling="no"
    ></iframe>
  </div>
</section>

{% schema %}
{
  "name": "IEFTINIT Retururi",
  "settings": [
    { "type": "text", "id": "heading", "label": "Titlu", "default": "Cerere de retur" },
    { "type": "text", "id": "subheading", "label": "Subtitlu", "default": "Returnezi rapid și simplu în maxim 14 zile." },
    { "type": "color", "id": "bg_color", "label": "Culoare fundal", "default": "#ffffff" },
    { "type": "color", "id": "text_color", "label": "Culoare text", "default": "#0a0a0c" }
  ],
  "presets": [{ "name": "IEFTINIT Retururi" }]
}
{% endschema %}`,
    },
    {
      key: "service",
      title: "Service / Garanție",
      filename: "ieftinit-service",
      sectionName: "IEFTINIT Service",
      description: "Pentru pagina de cereri service & garanție.",
      code: `{% comment %}
  IEFTINIT Control Hub — Service & Warranty Section (Online Store 2.0 compatible)
  Auto-iframes the hosted service request form in EMBED mode.
{% endcomment %}

<section class="ieftinit-service" style="padding: 24px 0; background: {{ section.settings.bg_color | default: '#ffffff' }};">
  <div style="max-width: 960px; margin: 0 auto; padding: 0 16px;">
    {% if section.settings.heading != blank %}
      <h2 style="text-align:center; font-size: clamp(24px, 4vw, 32px); margin: 0 0 8px; color: {{ section.settings.text_color | default: '#0a0a0c' }}; font-weight: 700; letter-spacing: -0.02em;">
        {{ section.settings.heading }}
      </h2>
    {% endif %}
    {% if section.settings.subheading != blank %}
      <p style="text-align:center; opacity:.65; margin: 0 0 24px; color: {{ section.settings.text_color | default: '#0a0a0c' }};">
        {{ section.settings.subheading }}
      </p>
    {% endif %}
    <iframe
      src="${origin}/service/public?embed=1"
      style="width:100%; min-height: 1100px; border:0; background: transparent; display: block;"
      title="Cerere service IEFTINIT"
      loading="lazy"
      allow="clipboard-write"
      scrolling="no"
    ></iframe>
  </div>
</section>

{% schema %}
{
  "name": "IEFTINIT Service",
  "settings": [
    { "type": "text", "id": "heading", "label": "Titlu", "default": "Cerere service / garanție" },
    { "type": "text", "id": "subheading", "label": "Subtitlu", "default": "Diagnoză gratuită · ridicare cu curier propriu." },
    { "type": "color", "id": "bg_color", "label": "Culoare fundal", "default": "#ffffff" },
    { "type": "color", "id": "text_color", "label": "Culoare text", "default": "#0a0a0c" }
  ],
  "presets": [{ "name": "IEFTINIT Service" }]
}
{% endschema %}`,
    },
  ];

  const copySnippet = (code, label) => {
    navigator.clipboard.writeText(code);
    toast.success(`Snippet "${label}" copiat în clipboard`);
  };

  return (
    <div className="space-y-6" data-testid="shopify-settings-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Setări · Integrări</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Shopify</h1>
        <p className="text-muted-foreground text-sm mt-1">Conectează magazinul tău Shopify la Control Hub via OAuth modern.</p>
      </div>

      {loading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <Card className="p-6 bg-card border-border" data-testid="connection-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4">
              <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${status?.connected ? "bg-emerald-500/10" : "bg-muted"}`}>
                <ShoppingBag className={`h-6 w-6 ${status?.connected ? "text-emerald-500" : "text-muted-foreground"}`} />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display font-semibold text-lg">
                    {status?.connected ? status.shop_info?.name || status.shop : "Nu este conectat"}
                  </h3>
                  {status?.connected ? (
                    <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                      <CircleDot className="h-3 w-3 mr-1" /> Conectat
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground"><X className="h-3 w-3 mr-1" /> Deconectat</Badge>
                  )}
                </div>
                {status?.connected ? (
                  <div className="text-sm text-muted-foreground space-y-0.5">
                    <div><span className="font-mono text-xs">{status.shop}</span></div>
                    <div className="text-xs">Plan: {status.shop_info?.plan_name || "—"} · Monedă: {status.shop_info?.currency || "—"} · Owner: {status.shop_info?.owner || "—"}</div>
                    <div className="text-xs">Instalat: {fmtRelative(status.installed_at)} · API {status.api_version}</div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">
                    Apasă <strong>Conectează</strong> pentru a porni OAuth flow-ul cu Shopify.
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status?.connected ? (
                <>
                  <Button variant="outline" size="sm" onClick={runHealth} disabled={healthLoading} data-testid="run-health-btn">
                    {healthLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
                    Verifică conexiune
                  </Button>
                  {canManage && health?.scope_diff?.missing?.length > 0 && (
                    <Button variant="outline" size="sm" onClick={reinstall} disabled={reinstalling} data-testid="reinstall-btn">
                      {reinstalling ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                      Reinstalează pentru noi scopes
                    </Button>
                  )}
                  {canManage && (
                    <Button variant="outline" size="sm" onClick={disconnect} className="text-red-500 hover:text-red-600 border-red-500/30" data-testid="disconnect-btn">
                      Deconectează
                    </Button>
                  )}
                </>
              ) : (
                canManage && (
                  <Button onClick={() => setOpenConnect(true)} data-testid="connect-btn">
                    <Plug className="h-4 w-4 mr-1.5" /> Conectează Shopify
                  </Button>
                )
              )}
            </div>
          </div>

          {/* Functional health probes */}
          {status?.connected && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Stare funcțională API</div>
                {health && (
                  <Badge className={health.functional_ok ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" : "bg-red-500/10 text-red-500 border-red-500/30"}>
                    {health.functional_ok ? <><Check className="h-3 w-3 mr-1" /> Operațional</> : <><AlertTriangle className="h-3 w-3 mr-1" /> Probleme</>}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {(health?.checks || [
                  { name: "access_scopes", ok: null }, { name: "shop", ok: null },
                  { name: "orders", ok: null }, { name: "customers", ok: null },
                ]).map((c) => (
                  <div key={c.name} className="p-3 border border-border rounded-md bg-secondary/30" data-testid={`probe-${c.name}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {c.ok === true ? <Check className="h-3.5 w-3.5 text-emerald-500" /> :
                       c.ok === false ? <X className="h-3.5 w-3.5 text-red-500" /> :
                       <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />}
                      <span className="text-xs font-mono">{c.name}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate" title={c.detail || ""}>
                      {c.detail || (healthLoading ? "verificare…" : "neverificat")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scopes diff (dynamic, real) */}
          {status?.connected && health?.scope_diff && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  Permisiuni · {health.scope_diff.granted_count}/{health.scope_diff.total} acordate
                </div>
                {health.scope_diff.missing.length === 0 && (
                  <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">
                    <Check className="h-3 w-3 mr-1" /> Toate scopes cerute sunt active
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Comparația se face live cu <span className="font-mono">/admin/oauth/access_scopes.json</span> și include implicații (ex: <span className="font-mono">write_orders</span> implică <span className="font-mono">read_orders</span>).
                {health.scope_diff.missing.length > 0 && !health.functional_ok && " Unele lipsesc — apasă Reinstalează."}
                {health.scope_diff.missing.length > 0 && health.functional_ok && " Unele scopes apar lipsă dar API-ul funcționează corect — pot fi limitări de plan (de ex. read_all_orders nu apare pe planuri sub Shopify Plus, dar orders query merge oricum)."}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {health.scope_diff.per_scope.map((s) => (
                  <Badge key={s.scope} variant="outline" className={
                    s.granted ? "border-emerald-500/30 text-emerald-500" : "border-amber-500/30 text-amber-500"
                  } data-testid={`scope-${s.scope}`}>
                    {s.granted ? <Check className="h-3 w-3 mr-1" /> : <AlertTriangle className="h-3 w-3 mr-1" />}
                    {s.scope}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Static scopes fallback (only when not connected) */}
          {!status?.connected && status?.scopes_requested && (
            <div className="mt-6 pt-6 border-t border-border">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Permisiuni cerute la instalare</div>
              <div className="flex flex-wrap gap-1.5">
                {status.scopes_requested.split(",").map((s) => (
                  <Badge key={s} variant="outline" className="border-border text-muted-foreground">{s.trim()}</Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <Tabs defaultValue="webhooks">
        <TabsList>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">Webhook-uri</TabsTrigger>
          <TabsTrigger value="embed" data-testid="tab-embed">Snippet Liquid</TabsTrigger>
          <TabsTrigger value="info" data-testid="tab-info">Configurație</TabsTrigger>
        </TabsList>

        <TabsContent value="webhooks">
          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-display font-semibold text-lg">Webhook-uri Shopify</h3>
                <p className="text-xs text-muted-foreground mt-1">Auto-înregistrate la instalare · HMAC-verified · procesare asincronă</p>
              </div>
              {status?.connected && (
                <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh</Button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {["orders/create", "orders/updated", "fulfillments/update", "app/uninstalled"].map((t) => (
                <div key={t} className="p-3 border border-border rounded-md bg-secondary/30">
                  <div className="text-xs font-mono">{t}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{status?.connected ? "Activ" : "În așteptare"}</div>
                </div>
              ))}
            </div>
            <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Evenimente recente</h4>
            {webhookLogs.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-6">Niciun webhook procesat încă.</div>
            ) : (
              <div className="divide-y divide-border text-sm">
                {webhookLogs.map((w) => (
                  <div key={w.id} className="py-2 flex items-center gap-3" data-testid={`webhook-log-${w.id}`}>
                    <Badge variant="outline" className={
                      w.status === "done" ? "border-emerald-500/30 text-emerald-500" :
                      w.status === "error" ? "border-red-500/30 text-red-500" :
                      "border-amber-500/30 text-amber-500"
                    }>{w.status}</Badge>
                    <span className="font-mono text-xs flex-1">{w.topic}</span>
                    <span className="text-xs text-muted-foreground">{fmtRelative(w.received_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="embed">
          <Card className="p-6 bg-card border-border">
            <div className="mb-6">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2"><Code2 className="h-5 w-5" /> Liquid Snippets</h3>
              <p className="text-xs text-muted-foreground mt-1">Snippet-uri OS 2.0 pentru fiecare modul. Adaugă-le în temă ca section-uri separate.</p>
              <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1 mt-4">
                <li>Mergi la <span className="font-mono text-foreground">Online Store → Themes → [tema ta] → Edit code</span></li>
                <li>Pentru fiecare snippet de mai jos, apasă <span className="font-mono text-foreground">Add new section</span> și folosește numele indicat</li>
                <li>Lipește codul și salvează</li>
                <li>Adaugă section-ul în pagina dorită din <span className="font-mono text-foreground">Customize → Add section</span></li>
              </ol>
            </div>

            <div className="space-y-6">
              {snippets.map((snip) => (
                <div key={snip.key} className="border border-border rounded-lg overflow-hidden" data-testid={`snippet-block-${snip.key}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-secondary/40 border-b border-border">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-display font-semibold text-sm">{snip.title}</span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono font-semibold px-1.5 py-0.5 rounded bg-background border border-border">
                          {snip.filename}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{snip.description}</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => copySnippet(snip.code, snip.title)} data-testid={`copy-snippet-${snip.key}`}>
                      <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiază
                    </Button>
                  </div>
                  <pre className="bg-secondary/20 p-4 text-xs overflow-x-auto font-mono max-h-[320px] scrollbar-thin m-0">
                    <code>{snip.code}</code>
                  </pre>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="info">
          <Card className="p-6 bg-card border-border space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">API Key</span><span className="font-mono text-xs">{status?.api_key || "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">API Version</span><span className="font-mono text-xs">{status?.api_version || "—"}</span></div>
            <div className="flex justify-between items-start gap-4"><span className="text-muted-foreground shrink-0">App URL</span><span className="font-mono text-xs break-all text-right">{status?.app_url || "—"}</span></div>
            <div className="flex justify-between items-start gap-4"><span className="text-muted-foreground shrink-0">Callback URL</span><span className="font-mono text-xs break-all text-right">{status?.app_url}/api/shopify/callback</span></div>
            <div className="flex justify-between items-start gap-4"><span className="text-muted-foreground shrink-0">Webhook URL</span><span className="font-mono text-xs break-all text-right">{status?.app_url}/api/shopify/webhooks</span></div>
            <div className="pt-3 border-t border-border text-xs text-muted-foreground">
              <strong>Important:</strong> În Shopify Partners Dashboard, asigură-te că URL-urile de App URL și Allowed redirection URLs conțin domeniul de mai sus.
              <a href="https://partners.shopify.com" target="_blank" rel="noopener noreferrer" className="text-primary inline-flex items-center gap-1 ml-1">Deschide Partners <ExternalLink className="h-3 w-3" /></a>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Connect dialog */}
      <Dialog open={openConnect} onOpenChange={setOpenConnect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conectează un magazin Shopify</DialogTitle>
            <DialogDescription>Vei fi redirectat către Shopify pentru a aproba permisiunile cerute. Tokenul de acces este criptat și salvat în baza de date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Domeniu .myshopify.com</Label>
              <Input value={shopInput} onChange={(e) => setShopInput(e.target.value)} placeholder="numele-tau.myshopify.com" className="font-mono" data-testid="shop-input" />
              <p className="text-xs text-muted-foreground">Vei fi redirectat către Shopify pentru a aproba permisiunile.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenConnect(false)} disabled={connecting}>Anulează</Button>
            <Button onClick={startInstall} disabled={connecting} data-testid="start-install-btn">
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continuă pe Shopify <ExternalLink className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
